<template>
  <div class="timeline-card">
    <div class="timeline-header">
      <h6 class="timeline-title">
        <v-icon name="md-timeline" scale="0.9" class="mr-2" />
        Historial de Transiciones
      </h6>
      <div class="timeline-actions">
        <button
          class="btn-timeline-action"
          :class="{ active: viewMode === 'timeline' }"
          @click="viewMode = 'timeline'"
          title="Vista timeline"
        >
          <v-icon name="md-timeline" scale="0.9" />
        </button>
        <button
          class="btn-timeline-action"
          :class="{ active: viewMode === 'list' }"
          @click="viewMode = 'list'"
          title="Vista lista"
        >
          <v-icon name="bi-list-ul" scale="0.9" />
        </button>
      </div>
    </div>

    <!-- Buscador de Contrato -->
    <div class="contract-search">
      <div class="search-input-wrapper">
        <v-icon name="bi-search" scale="0.8" class="search-icon" />
        <input
          type="text"
          v-model="searchContractId"
          placeholder="Ingrese ID de contrato para ver su historial completo..."
          class="search-input"
          @keyup.enter="searchContractHistory"
        />
        <button
          class="btn-search"
          @click="searchContractHistory"
          :disabled="!searchContractId || loadingHistory"
        >
          <span v-if="loadingHistory" class="spinner-border spinner-border-sm"></span>
          <span v-else>Buscar</span>
        </button>
      </div>
      <p class="search-hint">
        Busque un contrato específico para ver todo su historial de cambios de estado
      </p>
    </div>

    <!-- Historial del Contrato Buscado -->
    <div v-if="contractHistory.length > 0" class="contract-history-section">
      <div class="history-header">
        <h6 class="history-title">
          <v-icon name="bi-file-text" scale="0.9" class="mr-2" />
          Historial del Contrato: <span class="contract-id-badge">{{ selectedContractId }}</span>
        </h6>
        <span class="history-count">{{ contractHistory.length }} cambios de estado</span>
      </div>

      <div class="timeline-content">
        <!-- Vista Timeline -->
        <div v-if="viewMode === 'timeline'" class="timeline-view">
          <div
            v-for="(entry, index) in contractHistory"
            :key="index"
            class="timeline-item"
            :class="{ 'first': index === 0 }"
          >
            <div class="timeline-marker">
              <div class="marker-dot" :class="getTransitionClass(entry)"></div>
              <div class="marker-line" v-if="index < contractHistory.length - 1"></div>
            </div>
            <div class="timeline-card-content">
              <div class="transition-header">
                <span class="transition-number">#{{ contractHistory.length - index }}</span>
                <span class="transition-time">{{ formatTime(entry.transChangeDate) }}</span>
              </div>
              <div class="transition-flow">
                <span class="status-badge from" v-if="index < contractHistory.length - 1">
                  {{ formatStatusDescription(contractHistory[index + 1].statusDescription) }}
                </span>
                <span class="status-badge from" v-else>
                  (Estado inicial)
                </span>
                <v-icon name="md-arrowforward" scale="0.8" class="arrow-icon" />
                <span class="status-badge to">
                  {{ formatStatusDescription(entry.statusDescription) }}
                </span>
              </div>
              <div class="transition-meta">
                <span class="meta-item">
                  <v-icon name="fa-regular-user" scale="0.7" />
                  {{ entry.userUpdatedId || 'Sistema' }}
                </span>
                <span class="meta-item">
                  <v-icon name="md-schedule" scale="0.7" />
                  {{ formatDateTime(entry.transChangeDate) }}
                </span>
              </div>
              <div class="time-in-state" v-if="index < contractHistory.length - 1">
                <v-icon name="bi-clock" scale="0.7" />
                <span>Tiempo en este estado: {{ calculateTimeBetween(contractHistory[index + 1].transChangeDate, entry.transChangeDate) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Vista Lista -->
        <div v-else class="list-view">
          <table class="transitions-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Estado Anterior</th>
                <th>Estado Nuevo</th>
                <th>Fecha/Hora</th>
                <th>Usuario</th>
                <th>Tiempo en Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(entry, index) in contractHistory" :key="index">
                <td class="cell-number">{{ contractHistory.length - index }}</td>
                <td>
                  <span class="status-badge-mini from" v-if="index < contractHistory.length - 1">
                    {{ formatStatusDescription(contractHistory[index + 1].statusDescription) }}
                  </span>
                  <span class="status-badge-mini from" v-else>-</span>
                </td>
                <td>
                  <span class="status-badge-mini to">
                    {{ formatStatusDescription(entry.statusDescription) }}
                  </span>
                </td>
                <td class="cell-time">{{ formatDateTime(entry.transChangeDate) }}</td>
                <td class="cell-user">{{ entry.userUpdatedId || 'Sistema' }}</td>
                <td class="cell-duration">
                  <span v-if="index < contractHistory.length - 1">
                    {{ calculateTimeBetween(contractHistory[index + 1].transChangeDate, entry.transChangeDate) }}
                  </span>
                  <span v-else>-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Estado vacío cuando no hay búsqueda -->
    <div class="no-data" v-else-if="!loadingHistory && !searchContractId">
      <v-icon name="md-timeline" scale="3" class="text-muted" />
      <p>Ingrese un ID de contrato para ver su historial completo de transiciones</p>
    </div>

    <!-- Sin resultados -->
    <div class="no-data" v-else-if="!loadingHistory && searchContractId && searchPerformed">
      <v-icon name="bi-inbox" scale="3" class="text-muted" />
      <p>No se encontró historial para el contrato "{{ searchContractId }}"</p>
    </div>

    <!-- Loading -->
    <div class="loading-state" v-if="loadingHistory">
      <img src="@/assets/loaderPrimary.svg" height="50" alt="Cargando..." />
      <p>Cargando historial del contrato...</p>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "StateTransitionTimeline",
  props: {
    transitions: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    statusTranslations: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      viewMode: "timeline",
      searchContractId: "",
      selectedContractId: "",
      contractHistory: [],
      loadingHistory: false,
      searchPerformed: false,
    };
  },
  methods: {
    async searchContractHistory() {
      if (!this.searchContractId) return;

      this.loadingHistory = true;
      this.searchPerformed = true;
      this.contractHistory = [];

      try {
        const response = await axios.get(`contract/history/${this.searchContractId}`);
        const histories = response.data.contractHistories || [];

        // Ordenar por fecha descendente (más reciente primero)
        this.contractHistory = histories.sort((a, b) =>
          moment(b.transChangeDate).valueOf() - moment(a.transChangeDate).valueOf()
        );

        this.selectedContractId = this.searchContractId;
      } catch (error) {
        console.error("Error al obtener historial:", error);
        this.contractHistory = [];
      } finally {
        this.loadingHistory = false;
      }
    },

    formatStatusDescription(description) {
      if (!description) return "Desconocido";
      // El backend ya devuelve la descripción en español
      return description;
    },

    formatTime(timestamp) {
      if (!timestamp) return "";
      const date = moment(timestamp);
      const now = moment();
      const diffHours = now.diff(date, "hours");

      if (diffHours < 1) return "Hace unos minutos";
      if (diffHours < 24) return `Hace ${diffHours} hora${diffHours > 1 ? "s" : ""}`;
      if (diffHours < 48) return "Ayer";
      return date.format("DD/MM/YYYY HH:mm");
    },

    formatDateTime(timestamp) {
      if (!timestamp) return "";
      return moment(timestamp).format("DD/MM/YYYY HH:mm:ss");
    },

    calculateTimeBetween(startDate, endDate) {
      if (!startDate || !endDate) return "-";

      const start = moment(startDate);
      const end = moment(endDate);
      const diffMinutes = end.diff(start, "minutes");

      if (diffMinutes < 1) return "< 1 minuto";
      if (diffMinutes < 60) return `${diffMinutes} minuto${diffMinutes > 1 ? "s" : ""}`;

      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours < 24) {
        const mins = diffMinutes % 60;
        return `${diffHours}h ${mins}m`;
      }

      const diffDays = Math.floor(diffHours / 24);
      const hours = diffHours % 24;
      if (diffDays < 30) {
        return `${diffDays}d ${hours}h`;
      }

      const diffMonths = Math.floor(diffDays / 30);
      const days = diffDays % 30;
      return `${diffMonths} mes${diffMonths > 1 ? "es" : ""} ${days}d`;
    },

    getTransitionClass(entry) {
      const status = entry.statusDescription || "";
      const statusUpper = status.toUpperCase();

      // Estados positivos
      if (statusUpper.includes("ACTIVO") || statusUpper.includes("ACTIVE") ||
          statusUpper.includes("COMPLETADO") || statusUpper.includes("APROBADO") ||
          statusUpper.includes("ENTREGADO") || statusUpper.includes("CONFIRMADO")) {
        return "positive";
      }

      // Estados negativos
      if (statusUpper.includes("CANCELADO") || statusUpper.includes("RECHAZADO") ||
          statusUpper.includes("FALLIDO") || statusUpper.includes("EXPIRADO")) {
        return "negative";
      }

      return "neutral";
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../../scss/variables.scss';

.timeline-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

.timeline-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
}

.timeline-actions {
  display: flex;
  gap: 0.25rem;
}

.btn-timeline-action {
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #ffffff;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
  }

  &.active {
    background: $primary;
    border-color: $primary;
    color: #ffffff;
  }
}

// Buscador de contrato
.contract-search {
  background: #f9fafb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #9ca3af;
  z-index: 1;
}

.search-input {
  flex: 1;
  padding: 0.625rem 0.75rem 0.625rem 2.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.btn-search {
  padding: 0.625rem 1.25rem;
  background: $primary;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;

  &:hover:not(:disabled) {
    background: darken($primary, 10%);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.search-hint {
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0.5rem 0 0 0;
}

// Sección de historial
.contract-history-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 1rem;
  background: linear-gradient(135deg, rgba($primary, 0.05) 0%, rgba($primary, 0.02) 100%);
  border-bottom: 1px solid #e5e7eb;
}

.history-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
}

.contract-id-badge {
  background: $primary;
  color: #ffffff;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8125rem;
  margin-left: 0.5rem;
}

.history-count {
  font-size: 0.75rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
}

.timeline-content {
  max-height: 500px;
  overflow-y: auto;
  padding: 1rem;
}

// Timeline View
.timeline-view {
  padding-left: 0.5rem;
}

.timeline-item {
  display: flex;
  gap: 1rem;
  position: relative;

  &.first .timeline-card-content {
    border-color: $primary;
    background: linear-gradient(135deg, rgba($primary, 0.03) 0%, #ffffff 100%);
  }
}

.timeline-marker {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 20px;
}

.marker-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #e5e7eb;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 2px #e5e7eb;
  z-index: 1;

  &.positive {
    background: #10b981;
    box-shadow: 0 0 0 2px #d1fae5;
  }

  &.negative {
    background: #ef4444;
    box-shadow: 0 0 0 2px #fee2e2;
  }

  &.neutral {
    background: #3b82f6;
    box-shadow: 0 0 0 2px #dbeafe;
  }
}

.marker-line {
  width: 2px;
  flex: 1;
  background: #e5e7eb;
  margin-top: 4px;
  margin-bottom: 4px;
}

.timeline-card-content {
  flex: 1;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.875rem;
  margin-bottom: 0.75rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
}

.transition-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.transition-number {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
}

.transition-time {
  font-size: 0.75rem;
  color: #9ca3af;
}

.transition-flow {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.arrow-icon {
  color: #9ca3af;
}

.status-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.from {
    background: #f3f4f6;
    color: #6b7280;
  }

  &.to {
    background: linear-gradient(135deg, $primary 0%, darken($primary, 10%) 100%);
    color: #ffffff;
  }
}

.transition-meta {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.meta-item {
  font-size: 0.6875rem;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.time-in-state {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e5e7eb;
  font-size: 0.6875rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

// List View
.list-view {
  overflow-x: auto;
}

.transitions-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 0.75rem 0.5rem;
    text-align: left;
    border-bottom: 1px solid #f3f4f6;
  }

  th {
    font-size: 0.6875rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: #f9fafb;
  }

  td {
    font-size: 0.8125rem;
    color: #374151;
  }

  tr:hover td {
    background: #f9fafb;
  }
}

.cell-number {
  font-weight: 600;
  color: #6b7280;
  width: 40px;
}

.cell-time {
  color: #6b7280;
  font-size: 0.75rem;
  white-space: nowrap;
}

.cell-user {
  color: #6b7280;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cell-duration {
  font-size: 0.75rem;
  color: #374151;
  font-weight: 500;
}

.status-badge-mini {
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 0.125rem 0.375rem;
  border-radius: 3px;
  display: inline-block;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.from {
    background: #f3f4f6;
    color: #6b7280;
  }

  &.to {
    background: #dbeafe;
    color: #1d4ed8;
  }
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #9ca3af;

  p {
    margin-top: 0.75rem;
    font-size: 0.875rem;
    text-align: center;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  p {
    margin-top: 0.75rem;
    font-size: 0.875rem;
    color: #6b7280;
  }
}

@media (max-width: 768px) {
  .search-input-wrapper {
    flex-direction: column;
  }

  .search-input {
    width: 100%;
    padding-left: 2.25rem;
  }

  .btn-search {
    width: 100%;
  }

  .transition-flow {
    flex-direction: column;
    align-items: flex-start;
  }

  .status-badge {
    max-width: 100%;
  }
}
</style>
